// Per-member surcharge state for one shift. Containers populate one entry per
// member of the offer's target group. Until the API exposes per-member shift
// state on this surface (today the shift-list endpoint annotates against a
// single pro), containers fan out the same shift-level signal to every member;
// the aggregation primitive itself is per-member so the future API expansion
// drops in without touching the chip/warning dispatch.
export type MemberSurchargeState = {
  // True iff the member has a selected GigApplication for the shift.
  hasSelectedApp: boolean;
  // The frozen multiplier on that selected application. Null both for
  // admin-exempt selections and for members without an application.
  frozenMultiplier: number | null;
  // The is_high_demand predicate evaluated for this (member, shift) pair.
  isPredicateHighDemand: boolean;
};

export type GroupSurchargeOutcome =
  | { type: "chip"; multiplier: number; baseWage: number }
  | { type: "warning"; incurringCount: number; totalCount: number; multiplier: number; baseWage: number }
  | undefined;

type Classification = "incurs-frozen" | "exempt" | "predicate-incurs" | "no-incur";

const classifyMember = (m: MemberSurchargeState): Classification => {
  if (m.hasSelectedApp) return m.frozenMultiplier !== null ? "incurs-frozen" : "exempt";
  return m.isPredicateHighDemand ? "predicate-incurs" : "no-incur";
};

const incurs = (c: Classification) => c === "incurs-frozen" || c === "predicate-incurs";

type AggregateArgs = {
  members: ReadonlyArray<MemberSurchargeState>;
  baseWage: number | null | undefined;
  locationMultiplier: number | null | undefined;
};

// Pure aggregation — no location-toggle gating. Callers that need gating
// compose this with `useActiveLocation` and pass `undefined` for
// `locationMultiplier` when the toggle is off (see
// `SendDirectOfferContainer`). Tucker's option 4: 100% incur → chip; partial
// → "X of N members will incur surcharge" warning; 0% / no chip.
export const aggregateGroupHighDemandSurcharge = ({
  members,
  baseWage,
  locationMultiplier,
}: AggregateArgs): GroupSurchargeOutcome => {
  if (members.length === 0 || baseWage == null) return undefined;

  const classifications = members.map(classifyMember);
  const incurringCount = classifications.filter(incurs).length;

  if (incurringCount === 0) return undefined;

  // PaymentTerms multiplier is uniform per location, so a frozen value on
  // any member is consistent across all members. Prefer it when present so
  // the displayed math matches what's actually billed.
  const frozenIndex = classifications.indexOf("incurs-frozen");
  const frozen = frozenIndex >= 0 ? members[frozenIndex].frozenMultiplier : null;
  const multiplier = frozen ?? locationMultiplier;
  if (multiplier == null) return undefined;

  if (incurringCount === members.length) {
    return { type: "chip", multiplier, baseWage };
  }

  return { type: "warning", incurringCount, totalCount: members.length, multiplier, baseWage };
};
